import React from 'react'
import {
    Box,
    Center,
    Flex,
    Grid,
    Skeleton,
    Spinner,
    Text,
    useToken,
} from '@chakra-ui/react'
import dynamic from 'next/dynamic'
import { isGuest } from 'entities/user'
import { gqlErrorToStatusCode } from 'lib/error'
import { useAnalytics } from 'hooks/useAnalytics'
import { WithLikedJobs } from 'hooks/useLikedJobs'
import useMe from 'hooks/useMe'
import useQuery from 'hooks/useQuery'
import { SHARED_QUERY_JOB } from 'containers/upsert-job/queries'
import { ActionButtons } from 'containers/job/components/ActionButtons'
import { JobDetailsCard } from 'containers/job/components/JobDetailsCard'
import { JobHeader } from 'containers/job/components/JobHeader'
import { RelatedJobs } from 'containers/job/components/RelatedJobs'
import { StatsBoxWithData } from 'containers/job/components/StatsBox'
import { Tagline } from 'containers/job/components/Tagline'
import { TechStack } from 'containers/job/components/TechStack'
import { RelatedBlogs } from 'containers/job/components/blog/RelatedBlogs'
import { CompanyMiniCard } from 'containers/job/components/company/CompanyMiniCard'
import { IdealCandidate } from 'containers/job/components/idealCandidate'
import { SeoJob } from 'containers/job/components/seo/SeoJob'
import { useParams } from 'containers/job/hooks/useParams'
import { CardWrapper } from 'components/CardWrapper'
import { HtmlContent } from 'components/HtmlContent'
import AppShell from 'components/appShell'
import { CustomError } from 'components/error/CustomError'

const LazyApplyOnBehalfOf = dynamic(
    () => import('containers/job/components/ApplyOnBehalfOf'),
    {
        loading: () => <Skeleton height="72px" />,
        ssr: false,
    },
)

const LazyTrackingPixels = dynamic(
    () => import('containers/job/tracking-pixels/TrackingPixels'),
    {
        loading: () => null,
        ssr: false,
    },
)

const JobPageInner = () => {
    const currentUser = useMe()
    const params = useParams()
    const analytics = useAnalytics()

    const [result] = useQuery({
        query: SHARED_QUERY_JOB,
        variables: { slug: params.slug },
        pause: !params.slug,
    })

    if (result.fetching) {
        return (
            <Center maxWidth="900px" height="90vh" m="0 auto" p="12px">
                <Spinner size="xl" />
            </Center>
        )
    }

    const statusCode = gqlErrorToStatusCode(result.error)
    if (statusCode) {
        return <CustomError type={'job'} statusCode={statusCode} />
    }

    if (!result.data?.job.id) {
        return null
    }

    const job = result.data?.job

    const isGuestOrCandidate = isGuest(currentUser) || currentUser?.isCandidate
    const isCompanyUserOrAdmin = currentUser?.isCompanyUser || currentUser?.isAdmin

    const isPartnerJob = job.affiliate !== null
    const affiliateContentDisclaimer =
        job.affiliate?.source === 'jooble'
            ? 'Below is a snippet of the job description. To read the full text, please click on the "Apply Now" link.'
            : null

    return (
        <>
            <SeoJob job={job} />
            <Grid
                gridTemplateColumns={{
                    base: '100%',
                    job_content: '1fr minmax(auto, 375px)',
                }}
                mx={{
                    base: '16px',
                    sm: '24px',
                    md: '32px',
                    job_content: '24px',
                    xl: 0,
                }}
                my={{ base: '24px', md: '32px', xl: '64px' }}
                gridColumnGap={{ job_content: '40px', lg: '36px' }}
                gridRowGap={{ base: '36px', job_content: '88px' }}
                justifyContent={{ base: 'space-evenly', xl: 'space-between' }}
            >
                <Box
                    as={CardWrapper}
                    display={{ base: 'contents', job_content: 'flex' }}
                    flexDirection={{ job_content: 'column' }}
                    gridGap={{ job_content: '36px' }}
                    px={{ lg: '73px' }}
                    py="37px"
                    bgColor="white"
                    borderWidth={'0'}
                >
                    <Box
                        order={0}
                        as={CardWrapper}
                        borderWidth={'0'}
                        p={{ job_content: '0' }}
                    >
                        <JobHeader job={job} />
                    </Box>
                    <Box
                        order={4}
                        as={CardWrapper}
                        borderWidth={'0'}
                        padding={{ job_content: '0' }}
                        gridGap={{ base: '28px', md: '36px' }}
                    >
                        <Tagline tagline={job.tagline} />
                        <TechStack tags={job.tags} />
                        {affiliateContentDisclaimer && (
                            <Text variant="bodyTextMedium">
                                {affiliateContentDisclaimer}
                            </Text>
                        )}
                        <HtmlContent
                            html={job.descriptionHtml}
                            fontSize={'16px'}
                            lineHeight={'26px'}
                        />
                    </Box>
                    <Box order={5}>
                        <IdealCandidate job={job} />
                    </Box>
                </Box>
                <Box
                    display={{ base: 'contents', job_content: 'flex' }}
                    flexDirection={{ job_content: 'column' }}
                    gridGap={{ job_content: '36px' }}
                >
                    <Box
                        order={{ base: 7, job_content: isGuestOrCandidate ? 0 : 1 }}
                        display={{ base: 'block', md: 'none', job_content: 'block' }}
                    >
                        <CompanyMiniCard company={job.company} />
                    </Box>
                    <Flex
                        order={{ base: 1, job_content: isGuestOrCandidate ? 1 : 0 }}
                        direction="column"
                        gridGap={{ base: '28px', md: '36px' }}
                    >
                        {isCompanyUserOrAdmin && !isPartnerJob && (
                            <Flex gridGap="24px" order={{ base: 1, job_content: 0 }}>
                                <StatsBoxWithData job={job} currentUser={currentUser} />
                                {/* NB: Unfortunately, this code duplication is unavoidable. */}
                                <Box
                                    display={{
                                        base: 'none',
                                        md: 'block',
                                        job_content: 'none',
                                    }}
                                >
                                    <CompanyMiniCard company={job.company} />
                                </Box>
                            </Flex>
                        )}
                        <ActionButtons
                            job={job}
                            me={currentUser}
                            trackJobPublished={() =>
                                analytics?.track('Job published', { id: job?.id })
                            }
                            trackJobUnpublished={() =>
                                analytics?.track('Job unpublished', { id: job?.id })
                            }
                        />
                        {currentUser?.isAdmin && (
                            <CardWrapper borderWidth="0px">
                                <LazyApplyOnBehalfOf jobId={job.id} />
                            </CardWrapper>
                        )}
                    </Flex>
                    <Box order={{ base: 3, job_content: 2 }}>
                        <JobDetailsCard job={job} company={job.company} />
                    </Box>
                    <Box order={{ base: 9, job_content: 3 }}>
                        <RelatedBlogs companyId={job.company.id} numBlogs={2} />
                    </Box>
                </Box>
                <Box
                    gridColumn="1 / 3" // puts it at the bottom for desktop version
                    display={{ base: 'contents', job_content: 'flex' }}
                >
                    <Box order={{ base: 8, job_content: 0 }} width="100%">
                        <RelatedJobs jobId={job?.id} />
                    </Box>
                </Box>
            </Grid>
            <LazyTrackingPixels job={job} />
        </>
    )
}

export const Page = () => {
    const containerWidth = useToken('breakpoints', 'job_container')

    return (
        <>
            <AppShell
                title={'Job Page'}
                contentMaxWidth={containerWidth}
                backgroundColor="gray.background"
            >
                <WithLikedJobs>
                    <JobPageInner />
                </WithLikedJobs>
            </AppShell>
        </>
    )
}
